import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { Tab, TabNav } from '@aimsos/ui';

import { WIDGET_BUILDER_TABS } from '../constants/widgetBuilderTabs.js';

/**
 * `Data` | `Configure` (CT-5190, restructured 2026-09-08).
 *
 * `TabNav` + `Tab`, the underline bar both other studios use for page tabs —
 * `TruthPlaneDetailLayout.jsx`, `SandboxDetailTabs.jsx`, `HtlPackDetailPage.jsx`,
 * `KnowledgePackEditPage.jsx`. `SegmentedControl` was wrong here: in those repos
 * the pill rail is reserved for form controls and for switching views *inside* a
 * drawer or modal, never for tabs on a page. It also stretches its segments to
 * full width, which is why this bar spanned the whole column.
 *
 * No locking: a tab is a view of one form, not a step in a wizard. The old
 * stepper's per-step gates went with the third step.
 */
function WidgetBuilderTabs({ activeTab, onTabChange, disabled = false }) {
  const { t } = useTranslation('dashboards');

  return (
    <TabNav className="shrink-0" ariaLabel={t('widgetBuilder.tabs.ariaLabel')}>
      {WIDGET_BUILDER_TABS.map(({ id, labelKey }) => (
        <Tab
          key={id}
          size="sm"
          selected={activeTab === id}
          // Switching tabs mid-save would show a form the save is still writing.
          disabled={disabled && activeTab !== id}
          // The tab id is the payload, so one handler per tab beats a shared
          // one that has to read it back off the event target.
          onClick={() => onTabChange(id)}
        >
          {t(labelKey)}
        </Tab>
      ))}
    </TabNav>
  );
}

WidgetBuilderTabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  /** True while a save is running. */
  disabled: PropTypes.bool,
};

export default WidgetBuilderTabs;
